// =============================================================================
// domain/leakSequence.ts — two leaks read straight off the fills.
// -----------------------------------------------------------------------------
// The first pieces of the Coach's Report measurement engine. Both need NOTHING
// but the trade list — no excursion data, no Databento, no chart — which is why
// they come first: they work on every account from the day it imports.
//
//   TILT AFTER A LOSS      how much worse you trade once you are red
//   RIDING LOSERS          losers held past where your winners resolve
//
// ── 🚨 EVERY LEAK DECLARES TWO NUMBERS, AND THEY ARE NOT INTERCHANGEABLE ──
//
//   a RATE   — a per-trade or percentage figure, comparable across windows of
//              different length. This is what a rule is re-proved against.
//   a TOTAL  — dollars, meaningful only INSIDE one window.
//
// Ranking uses the total; proving uses the rate. The old client's comment says
// why, and it is the sort of thing that would otherwise be "simplified" away:
// a ten-day "since" window always shows a smaller TOTAL than a sixty-day
// baseline, so proving on totals would manufacture an improvement out of nothing
// but a shorter window.
//
// ── 🚨 `contributors` AND `population` ARE DIFFERENT NUMBERS ──
//
// The population is the sample the RATE divides by. The contributors are the
// trades that actually bled. Conflating them produces a readout that lies —
// "$18,324 across 216 trades" when only 90 of them gave anything back.
// =============================================================================

/** Everything these two leaks read. Deliberately narrow. */
export interface SequenceTrade {
  date: string;
  timeIn: string | null;
  timeOut: string | null;
  netPnl: number | null;
}

export interface LeakRate {
  /** Null when the sample cannot support the measure. NEVER zero as a stand-in. */
  value: number | null;
  n: number;
}

export interface LeakDollars {
  dollars: number;
  /** Trades that actually bled — the review set's size. */
  n: number;
  /** The sample the rate is computed over. */
  population: number;
  /**
   * Facts that must travel WITH the dollars, never separately.
   *
   * ⚠ A naked "$2,872 left behind" invites a naive hold-longer rule. "…and 68%
   * of those exits were protective" is the same fact told honestly, and the old
   * client attaches it for exactly that reason. The sentence itself is written at
   * the render site; only the facts live here.
   */
  meta?: {
    /** Share of the population where holding would have hit the stop. */
    stoppedPct?: number | null;
    /** True when the figure is a one-sided ceiling, not recoverable P&L. */
    upperBound?: boolean;
  };
}

const pnl = (t: SequenceTrade): number => Number(t?.netPnl) || 0;

// ---------------------------------------------------------------------------
// Sequence
// ---------------------------------------------------------------------------

/**
 * Split the book into trades taken AFTER a win and AFTER a loss.
 *
 * ⚠ SORTED BY DATE **AND ENTRY TIME**, because "the next trade" is a sequence
 * question and the stored order is whatever the importer produced. Sorting by
 * date alone would shuffle every trade within a day into an arbitrary order and
 * quietly measure a different thing each import.
 *
 * ⚠ SCRATCHES ARE SKIPPED ENTIRELY — they are neither a win nor a loss, and
 * they do not break the chain. A break-even trade between two losses must not
 * reset the run; the trader's state carries through it.
 */
export function sequenceSplit<T extends SequenceTrade>(trades: readonly T[]): {
  afterWin: T[]; afterLoss: T[];
} {
  const seq = [...trades].sort((a, b) => {
    const da = (a.date ?? '') + (a.timeIn ?? '');
    const db = (b.date ?? '') + (b.timeIn ?? '');
    return da < db ? -1 : da > db ? 1 : 0;
  });

  const afterWin: T[] = [];
  const afterLoss: T[] = [];
  let prev: 'w' | 'l' | null = null;

  for (const t of seq) {
    const p = pnl(t);
    const outcome = p > 0 ? 'w' : p < 0 ? 'l' : null;
    if (outcome === null) continue;
    if (prev === 'w') afterWin.push(t);
    else if (prev === 'l') afterLoss.push(t);
    prev = outcome;
  }
  return { afterWin, afterLoss };
}

const winRate = (ts: readonly SequenceTrade[]): number | null =>
  (ts.length ? (ts.filter((t) => pnl(t) > 0).length / ts.length) * 100 : null);

/** Each leg needs at least this many trades before the gap means anything. */
export const TILT_MIN_LEG = 3;

/**
 * Tilt, as the GAP between after-a-win and after-a-loss win rates.
 *
 * ── 🚨 A GAP, NOT A LEVEL, AND THAT IS THE WHOLE DESIGN ──
 *
 * Measuring the raw after-loss win rate would move with the trader's general
 * edge: get better across the board and that number rises too — so a rule aimed
 * at tilt would be credited for an improvement it had nothing to do with. The
 * drop controls for that, because a uniform improvement lifts both legs.
 *
 * ⚠ POSITIVE MEANS TILT. Higher is worse here, which is the opposite of most
 * percentages on the report.
 *
 * ⚠ Returns null unless BOTH legs clear the minimum. One leg of two trades makes
 * a 50-point "gap" out of a single outcome.
 */
export function tiltDrop(trades: readonly SequenceTrade[]): LeakRate {
  const { afterWin, afterLoss } = sequenceSplit(trades);
  if (afterWin.length < TILT_MIN_LEG || afterLoss.length < TILT_MIN_LEG) {
    return { value: null, n: 0 };
  }
  const w = winRate(afterWin);
  const l = winRate(afterLoss);
  if (w === null || l === null) return { value: null, n: 0 };
  return { value: w - l, n: Math.min(afterWin.length, afterLoss.length) };
}

/**
 * The dollar face of tilt: what the after-a-loss trades booked, when they lost.
 *
 * ⚠ ZERO WHEN THE AFTER-LOSS SET IS NET POSITIVE. If trading after a loss makes
 * money on balance there is no dollar leak to report, even if the win rate
 * dipped — and reporting the losers' total anyway would invent a cost out of a
 * profitable run.
 */
export function tiltDollars(trades: readonly SequenceTrade[]): LeakDollars {
  const after = sequenceSplit(trades).afterLoss;
  const net = after.reduce((s, t) => s + pnl(t), 0);
  const losers = after.filter((t) => pnl(t) < 0);
  return {
    dollars: net < 0 ? Math.abs(net) : 0,
    n: net < 0 ? losers.length : 0,
    population: after.length,
  };
}

/**
 * The trades to review — only when there is a net cost.
 *
 * ⚠ GENERIC ON THE ROW, and every contributor helper in this file and in
 * `leakPlan.ts` is now the same way. These return a SUBSET of what they were
 * handed, by reference — so declaring the return as the narrow measuring type
 * would throw away the caller's own row and the review list could not print a
 * contract, a price or a P&L it is holding in its hand. The measures stay narrow
 * because they genuinely read only these fields.
 */
export function tiltContributors<T extends SequenceTrade>(trades: readonly T[]): T[] {
  const after = sequenceSplit(trades).afterLoss;
  const net = after.reduce((s, t) => s + pnl(t), 0);
  return net < 0 ? after.filter((t) => pnl(t) < 0) : [];
}

// ---------------------------------------------------------------------------
// Disposition
// ---------------------------------------------------------------------------

/** Seconds since midnight, or null. */
function clockSeconds(clock: string | null | undefined): number | null {
  if (!clock) return null;
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(clock);
  if (!m) return null;
  const h = Number(m[1]); const min = Number(m[2]); const s = Number(m[3] ?? 0);
  if (!Number.isFinite(h) || !Number.isFinite(min)) return null;
  return h * 3600 + min * 60 + s;
}

/**
 * How long a trade was held, in minutes.
 *
 * ⚠ A NEGATIVE SPAN ROLLS FORWARD A DAY. These are clock times with no date on
 * the exit, so an overnight hold reads as a negative duration — and without the
 * roll a Globex trade entered at 22:10 and exited at 01:30 would count as −1,240
 * minutes and drag every median it touches.
 */
export function holdMinutes(t: SequenceTrade): number | null {
  const a = clockSeconds(t?.timeIn);
  const b = clockSeconds(t?.timeOut);
  if (a === null || b === null) return null;
  let d = b - a;
  if (d < 0) d += 86_400;
  return d / 60;
}

export function median(values: readonly number[]): number | null {
  if (!values.length) return null;
  const s = [...values].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? (s[m] as number) : (((s[m - 1] as number) + (s[m] as number)) / 2);
}

export interface DispositionStats<T extends SequenceTrade = SequenceTrade> {
  winN: number;
  loseN: number;
  winMedian: number | null;
  loseMedian: number | null;
  /** Loser median minus winner median. ⚠ POSITIVE = you ride losers. */
  gap: number | null;
  riddenDollars: number;
  riddenN: number;
  riddenTrades: T[];
}

/**
 * The disposition effect — losers held longer than winners.
 *
 * ⚠ SCRATCHES ARE EXCLUDED. A break-even trade carries no disposition signal
 * either way, and including it drags whichever median it lands in.
 *
 * ⚠ THE DOLLAR FACE IS THE **RIDDEN LOSERS**: losses booked on trades held past
 * the point where the winners typically resolve. That is the actionable set —
 * "cut these sooner" — rather than the whole losing column, most of which was
 * cut on time.
 */
export function dispositionStats<T extends SequenceTrade>(
  trades: readonly T[],
): DispositionStats<T> {
  const winHolds: number[] = [];
  const loseHolds: number[] = [];

  for (const t of trades) {
    const p = pnl(t);
    if (p === 0) continue;
    const h = holdMinutes(t);
    if (h === null) continue;
    (p > 0 ? winHolds : loseHolds).push(h);
  }

  const winMedian = median(winHolds);
  const loseMedian = median(loseHolds);
  const gap = (winMedian !== null && loseMedian !== null) ? loseMedian - winMedian : null;

  let riddenDollars = 0;
  let riddenN = 0;
  const riddenTrades: T[] = [];

  // ── 🔴 THE GAP HAS TO POINT THE RIGHT WAY FIRST (TCR-94) ──
  //
  // Josh, 2026-10-12, on being told this was his biggest leak: *"your analysis
  // and the app's analysis are complete opposites. I find that very interesting
  // and also a bit disturbing."* His own fills over five months: losers resolve
  // in a median 1.6 min against winners' 3.6 — a gap of −2.0, the opposite of
  // riding losers. Yet 40% of his losses were billed here.
  //
  // 🔴 THE BUG WAS THAT THIS LOOP DID NOT READ `gap` AT ALL. Any loser that ran
  // past the winner median was billed, and in a book where losers are cut FASTER
  // there are still some — so the dollar figure stayed large, and `rankLeaks`
  // sorts realized leaks on dollars alone. A trader got told to stop doing the
  // thing his own fills say he does not do.
  //
  // ⚠ A GATE, NOT A RESCALE. When the gap does not point at riding, the dollars,
  // the count and the review list are empty TOGETHER — gating further up in
  // `leaks.ts` instead would have left `riddenTrades` populated, so the review
  // column would list "ridden" losers under a leak reading $0.
  //
  // ⚠ `gap` ITSELF IS STILL REPORTED AS IT IS, negative included. That number is
  // the truth the trader should see, and it is what tells them by how much they
  // are doing the opposite.
  //
  // ⚠ The `winMedian !== null` half predates this and still matters on its own:
  // with no winners there is no "typical resolution" to compare against, and
  // treating every loser as ridden would put the whole losing column in review.
  if (winMedian !== null && gap !== null && gap > 0) {
    for (const t of trades) {
      const p = pnl(t);
      if (p >= 0) continue;
      const h = holdMinutes(t);
      if (h !== null && h > winMedian) {
        riddenDollars += Math.abs(p);
        riddenN += 1;
        riddenTrades.push(t);
      }
    }
  }

  return {
    winN: winHolds.length,
    loseN: loseHolds.length,
    winMedian,
    loseMedian,
    gap,
    riddenDollars,
    riddenN,
    riddenTrades,
  };
}

/** The rate: the hold-time gap in minutes. ⚠ Higher is worse. */
export function dispositionRate(trades: readonly SequenceTrade[]): LeakRate {
  const d = dispositionStats(trades);
  if (d.gap === null) return { value: null, n: 0 };
  // ⚠ `n` is the SMALLER leg. A gap resting on two winners is as thin as its
  // thinnest side, and reporting the total would overstate the sample.
  return { value: d.gap, n: Math.min(d.winN, d.loseN) };
}

export function dispositionDollars(trades: readonly SequenceTrade[]): LeakDollars {
  const d = dispositionStats(trades);
  return { dollars: d.riddenDollars, n: d.riddenN, population: d.winN + d.loseN };
}
